import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { formatInTimeZone } from 'date-fns-tz'

import { createInstance } from '@/helpers/api/base'
import { useGetUserInfo } from '@/modules/Auth'
import { useNotification } from '@/ui/Notification'
import { useModal } from '@/components/Modal'
import { useWorkstationStore } from '../store'

interface IReassignWorkstationRoute {
    author: string
    date: string
    route: string
    workstation: string
    currentWorkstation: string
}

interface Props {
    route: string
    workstation: string
}

export const useInvalidateControllerLogsAfterReassigned = () => {
    const queryClient = useQueryClient()

    return async () => {
        await queryClient.invalidateQueries({ queryKey: ['HUBS'] })
        await queryClient.invalidateQueries({ queryKey: ['controllerLogs'] })
    }
}

export const useReassignWorkstationRoute = (props?: Props) => {
    const { data: userInfo } = useGetUserInfo()
    const { workstation } = useWorkstationStore()
    const { closeModal } = useModal()
    const { showNotification } = useNotification()
    const invalidateControllerLogsAfterReassigned = useInvalidateControllerLogsAfterReassigned()

    const reassignRouteMutation = useAppMutation<unknown, Error, IReassignWorkstationRoute>({
        mutationFn: (data) =>
            createInstance({
                url: '/workstations/routes',
                method: 'POST',
                data,
            }),
        onSuccess: async () => {
            await invalidateControllerLogsAfterReassigned()
            closeModal()

            showNotification({ message: 'Route moved', type: 'submit', size: 'small' })
        },
        onError: (_err: Error, variables: IReassignWorkstationRoute) => {
            closeModal()
            showNotification({
                title: 'Route not moved',
                message: `Route ${variables.route} could not be transferred to your workstation due to an error. Please wait 10 minutes before you try again.`,
                type: 'error',
                size: 'small',
                manualDismiss: true,
                errorDismissTitle: 'Close',
            })
        },
    })

    const handleReassignWorkstationRoute = () => {
        if (!props) {
            return
        }

        reassignRouteMutation.mutate({
            ...props,
            workstation: workstation?.workstationNumber || '',
            currentWorkstation: props.workstation,
            author: userInfo?.lastSessionDetails.authorName || '',
            date: formatInTimeZone(new Date(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss"),
        })
    }

    return {
        handleReassignWorkstationRoute,
        isPending: reassignRouteMutation.isPending,
    }
}
